import { Fragment } from "react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUp, ArrowDown, ChevronsUpDown, MoreVertical, ChevronLeft, ChevronRight } from "lucide-react";
import { ErrorState, CardSkeleton } from "@/components/ListStates";

/**
 * Wiederverwendbare Tabelle (Phase 2): Sticky-Header, sortierbare Spalten,
 * Mehrfachauswahl, Zeilenaktionen (3-Punkte-Menü), Skeleton-Ladezustand,
 * Leerzustand und Pagination. Datenhaltung liegt beim Aufrufer.
 */
export function DataTable({
  columns,
  rows,
  loading,
  rowKey = (r) => r.id,
  sort,
  onSort,
  visibleColumns,
  selection,
  onToggleRow,
  onToggleAll,
  onRowClick,
  rowActions,
  emptyState,
  error,
  onRetry,
  mobileCard,
  skeletonRows = 8,
  // pagination
  page, pageSize, total, onPageChange, onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  testid = "data-table",
}) {
  const cols = (visibleColumns || columns.map((c) => c.key))
    .map((k) => columns.find((c) => c.key === k))
    .filter(Boolean);

  const allSelected = rows.length > 0 && rows.every((r) => selection?.has(rowKey(r)));
  const someSelected = rows.some((r) => selection?.has(rowKey(r)));
  const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 1)));

  const SortIcon = ({ colKey }) => {
    if (sort?.by !== colKey) return <ChevronsUpDown className="w-3.5 h-3.5 opacity-40" />;
    return sort.dir === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />;
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid={testid}>
      {/* Mobile Kartenliste (Phase C): unter sm werden Karten statt Tabelle gezeigt */}
      {mobileCard && (
        <div className="sm:hidden">
          {loading ? (
            <div className="p-3"><CardSkeleton count={6} /></div>
          ) : error ? (
            <div className="py-14 px-4"><ErrorState message={error} onRetry={onRetry} /></div>
          ) : rows.length === 0 ? (
            <div className="py-14 px-4">{emptyState}</div>
          ) : (
            <div className="divide-y divide-border">
              {rows.map((row) => {
                const key = rowKey(row);
                const selected = selection?.has(key);
                const actions = rowActions ? rowActions(row) : null;
                return (
                  <div
                    key={key}
                    className={`relative p-4 ${onRowClick ? "active:bg-muted/50 cursor-pointer" : ""}`}
                    onClick={onRowClick ? (e) => { if (!e.target.closest("[data-no-row-click]")) onRowClick(row); } : undefined}
                    data-testid={`dt-card-${key}`}
                  >
                    <div className="flex items-start gap-3">
                      {selection && (
                        <div data-no-row-click onClick={(e) => e.stopPropagation()} className="pt-0.5">
                          <Checkbox checked={!!selected} onCheckedChange={() => onToggleRow(key)} aria-label="Zeile auswählen" data-testid={`dt-card-select-${key}`} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">{mobileCard(row)}</div>
                      {actions && (
                        <div data-no-row-click onClick={(e) => e.stopPropagation()} className="shrink-0">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 -mr-1" data-testid={`dt-card-actions-${key}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              {actions.map((a) => (
                                <Fragment key={a.label}>
                                  {a.separatorBefore && <DropdownMenuSeparator />}
                                  <DropdownMenuItem onClick={a.onClick} className={a.danger ? "text-destructive focus:text-destructive" : ""} data-testid={`dt-card-action-${a.label}`}>
                                    {a.icon && <a.icon className="w-4 h-4 mr-2" />} {a.label}
                                  </DropdownMenuItem>
                                </Fragment>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className={`overflow-x-auto ${mobileCard ? "hidden sm:block" : ""}`}>
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-muted/60 backdrop-blur">
            <TableRow className="hover:bg-transparent">
              {selection && (
                <TableHead className="w-10">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                    onCheckedChange={onToggleAll}
                    aria-label="Alle auswählen"
                    data-testid="dt-select-all"
                  />
                </TableHead>
              )}
              {cols.map((c) => (
                <TableHead key={c.key} className={c.headClassName} style={c.width ? { width: c.width } : undefined}>
                  {c.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort?.(c.key)}
                      className="inline-flex items-center gap-1.5 font-semibold hover:text-foreground transition-colors"
                      data-testid={`dt-sort-${c.key}`}
                    >
                      {c.header} <SortIcon colKey={c.key} />
                    </button>
                  ) : (
                    <span className="font-semibold">{c.header}</span>
                  )}
                </TableHead>
              ))}
              {rowActions && <TableHead className="w-12 text-right" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: skeletonRows }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  {selection && <TableCell><div className="h-4 w-4 rounded bg-muted animate-pulse" /></TableCell>}
                  {cols.map((c) => (
                    <TableCell key={c.key}><div className="h-4 rounded bg-muted animate-pulse" style={{ width: `${40 + ((i * 17 + c.key.length * 7) % 45)}%` }} /></TableCell>
                  ))}
                  {rowActions && <TableCell />}
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={cols.length + (selection ? 1 : 0) + (rowActions ? 1 : 0)}>
                  <div className="py-14"><ErrorState message={error} onRetry={onRetry} /></div>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={cols.length + (selection ? 1 : 0) + (rowActions ? 1 : 0)}>
                  <div className="py-14">{emptyState}</div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const key = rowKey(row);
                const selected = selection?.has(key);
                const actions = rowActions ? rowActions(row) : null;
                return (
                  <TableRow
                    key={key}
                    data-state={selected ? "selected" : undefined}
                    className={onRowClick ? "cursor-pointer" : ""}
                    onClick={onRowClick ? (e) => { if (!e.target.closest("[data-no-row-click]")) onRowClick(row); } : undefined}
                    data-testid={`dt-row-${key}`}
                  >
                    {selection && (
                      <TableCell data-no-row-click onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={!!selected} onCheckedChange={() => onToggleRow(key)} aria-label="Zeile auswählen" data-testid={`dt-select-${key}`} />
                      </TableCell>
                    )}
                    {cols.map((c) => (
                      <TableCell key={c.key} className={c.className}>
                        {c.render ? c.render(row) : (row[c.key] ?? <span className="text-muted-foreground">—</span>)}
                      </TableCell>
                    ))}
                    {actions && (
                      <TableCell className="text-right" data-no-row-click onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`dt-actions-${key}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            {actions.map((a, i) => (
                              <Fragment key={a.label}>
                                {a.separatorBefore && <DropdownMenuSeparator />}
                                <DropdownMenuItem onClick={a.onClick} className={a.danger ? "text-destructive focus:text-destructive" : ""} data-testid={`dt-action-${a.label}`}>
                                  {a.icon && <a.icon className="w-4 h-4 mr-2" />} {a.label}
                                </DropdownMenuItem>
                              </Fragment>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {onPageChange && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-border text-sm">
          <div className="text-muted-foreground" data-testid="dt-total">
            {loading ? "Wird geladen…" : `${total} Einträge`}
            {someSelected && ` · ${Array.from(selection).length} ausgewählt`}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground hidden sm:inline">Pro Seite</span>
              <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
                <SelectTrigger className="h-8 w-[72px]" data-testid="dt-page-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => onPageChange(page - 1)} data-testid="dt-prev-page" aria-label="Vorherige Seite">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-2 tabular-nums" data-testid="dt-page-info">{page} / {totalPages}</span>
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} data-testid="dt-next-page" aria-label="Nächste Seite">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
